import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassRoomsApi, type SearchQueryRequest } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface IUseClassRoomsGetProps {
  page?: number;
  pageSize?: number;
  active?: boolean;
  searchQueryRequest?: SearchQueryRequest | null;
  enabled?: boolean;
}

export const useClassRoomsGet = ({
  page = 1,
  pageSize = 100,
  active,
  searchQueryRequest,
  enabled: enabledProp = true,
}: IUseClassRoomsGetProps = {}) => {
  const accessToken = useAccessToken();
  const useSearch =
    searchQueryRequest != null &&
    Array.isArray(searchQueryRequest.criteria) &&
    searchQueryRequest.criteria.length > 0;

  const { data, isError, isLoading, isSuccess, isPending, error } = useQuery({
    queryKey: [QueryKeys.ClassRooms, useSearch ? "search" : "scroll", page, pageSize, active, searchQueryRequest],
    enabled: !!accessToken && enabledProp,
    queryFn: () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(ClassRoomsApi, accessToken);
      if (useSearch) {
        return service.apiClassRoomsSearchPost({
          page,
          pageSize,
          active,
          searchQueryRequest,
        });
      }
      return service.apiClassRoomsScrollGet({
        page,
        pageSize,
        active,
      });
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    error,
  };
};
